import { Link } from "react-router-dom";
import { ArrowRight, Wrench, Plane, Radio, Eye, FileCheck } from "lucide-react";
import { motion } from "framer-motion";

const programmes = [
  {
    name: "TALON",
    type: "Fixed-Wing C-UAS Interceptor",
    maturity: "Development",
    description: "Fixed-wing interceptor design for counter-UAS applications. Designed for high-speed target engagement in GPS-denied environments.",
    evidence: "Design studies, computational analysis, sub-system testing",
    commercialRoute: "Licensable design asset, development partnership, or programme integration",
    nextStep: "Platform integration and flight testing programme",
    icon: Plane,
  },
  {
    name: "Optical-RF Fusion",
    type: "Sensor Integration",
    maturity: "Research",
    description: "Multi-modal sensor fusion combining optical tracking with RF detection for enhanced target acquisition and tracking.",
    evidence: "Algorithm development, simulation studies",
    commercialRoute: "Integration into qualified platforms through partnership",
    nextStep: "Hardware integration and field validation",
    icon: Eye,
  },
  {
    name: "Autonomous Coordination",
    type: "Multi-Agent Systems",
    maturity: "Research",
    description: "Coordination protocols for multi-agent autonomous operations. Distributed decision-making with human oversight.",
    evidence: "Simulation studies, algorithm validation",
    commercialRoute: "Technology licensing, development partnership",
    nextStep: "Platform integration trials",
    icon: Radio,
  },
];

export default function AdvancedDevelopmentPage() {
  return (
    <div className="relative">
      {/* Hero */}
      <section className="pt-32 pb-16 relative">
        <div className="absolute inset-0 grid-pattern opacity-50" />
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Link to="/platforms" className="text-caption hover:text-foreground transition-colors mb-4 inline-block">
              Platforms & Technology
            </Link>
            <div className="flex items-center gap-3 mb-4">
              <div className="h-12 w-12 rounded border border-border bg-muted/50 flex items-center justify-center">
                <Wrench className="h-6 w-6 text-muted-foreground" />
              </div>
            </div>
            <h1 className="text-display text-foreground mb-6">
              Advanced Development
            </h1>
            <p className="text-subtitle max-w-2xl mb-4">
              Design Assets & Development Programmes
            </p>
            <p className="text-body max-w-2xl">
              This section contains development programmes and licensable design assets that are not production-ready platforms. Each entry shows maturity, available evidence, commercial route and next development step.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Important Notice */}
      <section className="section-padding">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="max-w-3xl mx-auto">
            <div className="card-pillar p-8 border-l-2 border-l-muted-foreground">
              <div className="flex items-center gap-3 mb-4">
                <FileCheck className="h-5 w-5 text-muted-foreground" />
                <h2 className="text-label text-foreground">Important Notice</h2>
              </div>
              <p className="text-body">
                Items in this section are development programmes, not deployed products. Maturity indicators distinguish between research, development, and production readiness. This prevents concepts and licensable assets from being mistaken for deployed systems.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Programmes */}
      <section className="section-padding-lg bg-card/30 edge-glow">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="text-center mb-12">
            <p className="text-caption mb-4">Programmes</p>
            <h2 className="text-headline text-foreground">Active Development</h2>
          </div>
          
          <div className="space-y-6 max-w-4xl mx-auto">
            {programmes.map((prog, index) => (
              <motion.div
                key={index}
                className="card-pillar p-8"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded border border-border bg-muted/50 flex items-center justify-center">
                      <prog.icon className="h-5 w-5 text-muted-foreground" />
                    </div>
                    <div>
                      <h3 className="text-label text-foreground">{prog.name}</h3>
                      <p className="text-xs text-muted-foreground">{prog.type}</p>
                    </div>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded ${
                    prog.maturity === 'Development' 
                      ? 'bg-muted/80 text-foreground' 
                      : 'bg-muted/50 text-muted-foreground'
                  }`}>
                    {prog.maturity}
                  </span>
                </div>
                
                <p className="text-body mb-6">{prog.description}</p>
                
                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Evidence</p>
                    <p className="text-sm text-muted-foreground">{prog.evidence}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Commercial Route</p>
                    <p className="text-sm text-muted-foreground">{prog.commercialRoute}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Next Step</p>
                    <p className="text-sm text-muted-foreground">{prog.nextStep}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding-lg">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-headline text-foreground mb-6">
              Discuss a Development Partnership
            </h2>
            <p className="text-body mb-8">
              Interested in licensing a design asset or partnering on a development programme? Tell us about your requirements and we will scope a commercial approach.
            </p>
            <Link to="/contact?type=partnership" className="btn-primary">
              Discuss Partnership
              <ArrowRight className="h-4 w-4 ml-2" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
